<template>
  <transition name="fade-zoom">
    <div class="replay-modal-overlay" v-if="visible">
      <div class="replay-modal-card medical-modern" ref="modalContent">
        <!-- Actions en haut à droite -->
        <div class="modal-actions">
          <!-- Bouton imprimer -->
          <button class="print-btn" @click="printModal" aria-label="Imprimer cette analyse">
            <i class="fas fa-print"></i>
            <span class="print-label">Imprimer</span>
          </button>
          <!-- Bouton fermer -->
          <button class="close-btn" @click="$emit('close')" aria-label="Fermer">&times;</button>
        </div>
        
        <!-- En-tête médical -->
        <div class="medical-header">
          <div class="session-info">
            <h2 class="chart-title">Analyse de Variabilité Cardiaque</h2>
            <div class="session-meta">
              <span class="session-duration">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2"/>
                  <polyline points="12,6 12,12 16,14" stroke="currentColor" stroke-width="2"/>
                </svg>
                Durée : {{ formatDuration(session.totalDuration) }}
              </span>
            </div>
          </div>
        </div>

        <!-- Zone graphique -->
        <div class="chart-section">
          <div class="chart-legend">
            <div class="legend-item">
              <span class="legend-indicator hrv"></span>
              <span class="legend-label">Tachogramme (ms)</span>
            </div>
            <div class="legend-item">
              <span class="legend-indicator coherence"></span>
              <span class="legend-label">Cohérence cardiaque (%)</span>
            </div>
          </div>

          <div class="chart-wrapper">
            <svg :viewBox="'0 0 800 240'" class="medical-chart" preserveAspectRatio="none">
              <!-- Grille de fond optimisée -->
              <defs>
                <pattern id="grid" width="80" height="24" patternUnits="userSpaceOnUse">
                  <path d="M 80 0 L 0 0 0 24" fill="none" stroke="#f8f9fa" stroke-width="1"/>
                </pattern>
              </defs>
              <rect x="50" y="20" width="720" height="192" fill="url(#grid)"/>
              
              <!-- Axes principaux -->
              <line x1="50" y1="20" x2="50" y2="212" stroke="#e9ecef" stroke-width="1.5"/>
              <line x1="50" y1="212" x2="770" y2="212" stroke="#e9ecef" stroke-width="1.5"/>
              
              <!-- Zone de cohérence -->
              <polygon :points="coherenceAreaPoints" fill="#dc3545" opacity="0.06" stroke="none"/>
              
              <!-- Courbe de cohérence (rouge) -->
              <polyline :points="coherenceLinePoints" fill="none" stroke="#dc3545" stroke-width="0.6" stroke-linecap="round"/>
              
              <!-- Courbe tachogramme (bleu) -->
              <polyline :points="tachogramPoints" fill="none" stroke="#0d6efd" stroke-width="1" stroke-linecap="round"/>
              
              <!-- Graduations Y -->
              <g class="y-axis">
                <text v-for="y in yLabels" :key="'y-' + y" 
                      x="45" :y="yToSvg(y) + 3" 
                      class="axis-label">{{ y }}</text>
              </g>
              
              <!-- Graduations X -->
              <g class="x-axis">
                <text v-for="(t, i) in xTicks" :key="'x-' + i" 
                      :x="tickToX(t)" y="228" 
                      class="axis-label">{{ formatTickLabel(t) }}</text>
              </g>
            </svg>
          </div>
        </div>

        <ReplayDashboard :session="session"/>
        
        <!-- Champ commentaire dépliable -->
        <div class="comment-section" style="padding: 0 32px 16px 32px;">
          <button
            class="toggle-comment-btn"
            @click="showComment = !showComment"
            type="button"
            aria-expanded="showComment"
          >
            <i :class="showComment ? 'fas fa-chevron-up' : 'fas fa-chevron-down'"></i>
            <span style="margin-left:7px;">Ajouter un commentaire (optionnel)</span>
          </button>
          <transition name="fade">
            <div v-if="showComment" class="comment-collapse">
              <label for="comment-textarea" class="comment-label">Commentaire (optionnel)</label>
              <textarea
                id="comment-textarea"
                v-model="comment"
                class="comment-textarea"
                rows="3"
                placeholder="Ajoutez un commentaire sur cette analyse..."
                style="width:100%;resize:vertical;margin-top:6px;"
              ></textarea>
            </div>
          </transition>
        </div>
        <!-- Pied de page minimal -->
      <div class="modal-footer">
          <button class="btn-save" @click="saveExercise">Sauvegarder l'exercice</button>
          <button class="btn-close-analyse" @click="$emit('close')">Fermer l'analyse</button>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import ReplayDashboard from './ReplayDashboard.vue';
export default {
  name: "ReplayModalMedical",
  props: {
    visible: { type: Boolean, default: false },
    session: { type: Object, required: true }
  },
  components: {
    ReplayDashboard
  },
  data() {
    return {
      comment: "",
      showComment: false
    };
  },
  computed: {
    tachogramPoints() {
      const arr = this.session.ArrayTacho || [];
      if (!arr.length) return "";
      const xStart = arr[0]?.x || 0;
      const xEnd = arr[arr.length - 1]?.x || 1;
      const dataMin = Math.min(...arr.map((p) => p.y));
      const dataMax = Math.max(...arr.map((p) => p.y));
      return arr
        .map((p) => {
          const x = 50 + (720 * (p.x - xStart)) / (xEnd - xStart || 1);
          const y = 212 - 192 * ((p.y - dataMin) / (dataMax - dataMin || 1));
          return `${x},${y}`;
        })
        .join(" ");
    },
    coherenceAreaPoints() {
      const arr = this.session.TabCC || [];
      if (!arr.length) return "";
      const xStart = arr[0]?.x || 0;
      const xEnd = arr[arr.length - 1]?.x || 1;
      const dataMin = Math.min(...arr.map((p) => p.y));
      const dataMax = Math.max(...arr.map((p) => p.y));
      const points = arr.map((p) => {
        const x = 50 + (720 * (p.x - xStart)) / (xEnd - xStart || 1);
        const y = 212 - 192 * ((p.y - dataMin) / (dataMax - dataMin || 1));
        return `${x},${y}`;
      });
      points.push(`770,212`, `50,212`);
      return points.join(" ");
    },
    coherenceLinePoints() {
      const arr = this.session.TabCC || [];
      if (!arr.length) return "";
      const xStart = arr[0]?.x || 0;
      const xEnd = arr[arr.length - 1]?.x || 1;
      const dataMin = Math.min(...arr.map((p) => p.y));
      const dataMax = Math.max(...arr.map((p) => p.y));
      return arr
        .map((p) => {
          const x = 50 + (720 * (p.x - xStart)) / (xEnd - xStart || 1);
          const y = 212 - 192 * ((p.y - dataMin) / (dataMax - dataMin || 1));
          return `${x},${y}`;
        })
        .join(" ");
    },
    yLabels() {
      const arr = [...(this.session.ArrayTacho || []), ...(this.session.TabCC || [])];
      if (!arr.length) return [];
      const dataMin = Math.floor(Math.min(...arr.map((p) => p.y)) / 10) * 10 - 10;
      const dataMax = Math.ceil(Math.max(...arr.map((p) => p.y)) / 10) * 10 + 10;
      const step = Math.round((dataMax - dataMin) / 6) || 1;
      return Array.from({ length: 7 }, (_, i) => dataMax - i * step);
    },
    yToSvg() {
      const arr = [...(this.session.ArrayTacho || []), ...(this.session.TabCC || [])];
      if (!arr.length) return () => 212;
      const dataMin = Math.floor(Math.min(...arr.map((p) => p.y)) / 10) * 10 - 10;
      const dataMax = Math.ceil(Math.max(...arr.map((p) => p.y)) / 10) * 10 + 10;
      return (y) => 212 - 192 * ((y - dataMin) / (dataMax - dataMin || 1));
    },
    xTicks() {
      const arr = this.session.ArrayTacho || [];
      if (!arr.length) return [];
      const tickCount = 8;
      const total = arr.length;
      return Array.from({ length: tickCount }, (_, i) =>
        Math.round((i * (total - 1)) / (tickCount - 1))
      );
    },
    tickToX() {
      const arr = this.session.ArrayTacho || [];
      if (!arr.length) return () => 50;
      const xStart = arr[0]?.x;
      const xEnd = arr[arr.length - 1]?.x;
      return (idx) => {
        if (arr[idx]) {
          return 50 + (720 * (arr[idx].x - xStart)) / (xEnd - xStart || 1);
        }
        return 50;
      };
    },
    bpmDash() {
      const pct = Math.min(1, this.session.BpmCC / 150);
      return 157 * (1 - pct);
    },
    cohDash() {
      const pct = Math.min(1, this.session.AverageCC / 100);
      return 157 * (1 - pct);
    }
  },
  methods: {
    formatDuration(s) {
      if (!s || isNaN(s)) return "0:00";
      const m = Math.floor(s / 60);
      const sec = s % 60;
      return `${m}:${sec.toString().padStart(2, "0")}`;
    },
    formatTickLabel(idx) {
      const arr = this.session.ArrayTacho || [];
      if (!arr[idx]) return "";
      const totalSecs = this.session.totalDuration || Math.floor(arr.length / 8);
      const t = Math.round((idx * totalSecs) / (arr.length - 1));
      const m = Math.floor(t / 60);
      const s = t % 60;
      return m > 0 ? `${m}:${s.toString().padStart(2, "0")}` : `${s}s`;
    },
    saveExercise() {
      // Émettre l'événement pour sauvegarder les données avec le commentaire
      this.$emit("save-exercise", { ...this.session, comment: this.comment });
      this.$emit('close');  // Ferme la modale après sauvegarde
    },
    printModal() {
      const content = this.$refs.modalContent;
      if (!content) return;

      const printContents = content.cloneNode(true);
      const actions = printContents.querySelector('.modal-actions');
      if (actions) actions.parentNode.removeChild(actions);

      const printWindow = window.open('', '', 'width=900,height=700');
      const cssLinks = Array.from(document.querySelectorAll('link[rel="stylesheet"], style')).map(el => el.outerHTML).join('\n');

      printWindow.document.write(`
        <html>
          <head>
            <title>Impression - Analyse de Variabilité Cardiaque</title>
            ${cssLinks}
            <link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.5.0/css/all.min.css">
            <style>
              @media print {
                body {
                  background: #fff !important;
                }
                .replay-modal-card.medical-modern {
                  box-shadow: none !important;
                  border: none !important;
                  max-width: 100vw !important;
                  width: 100vw !important;
                  max-height: unset !important;
                  min-height: 90vh;
                }
                .modal-footer {
                  border-top: none !important;
                  background: #fff !important;
                }
              }
              button, .modal-actions {
                display: none !important;
              }
              .close-btn {
                display: none !important;
              }
              .chart-section, .dashboard-inline, .modal-footer {
                page-break-inside: avoid;
              }
            </style>
          </head>
          <body>
            ${printContents.outerHTML}
            <script>
              window.onload = function() { setTimeout(function(){ window.print(); window.close(); }, 100); }
            <\/script>
          </body>
        </html>
      `);
      printWindow.document.close();
    }
  }
};
</script>

<style scoped>
.replay-modal-overlay {
  position: fixed;
  z-index: 2000;
  inset: 0;
  background: rgba(15, 23, 42, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(3px);
}

.replay-modal-card.medical-modern {
  background: #ffffff;
  border-radius: 16px;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
  width: 95vw;
  max-width: 1200px;
  max-height: 95vh;
  overflow: hidden;
  position: relative;
  display: flex;
  flex-direction: column;
  border: 1px solid #e2e8f0;
}

/* Actions en haut à droite */
.modal-actions {
  position: absolute;
  top: 18px;
  right: 20px;
  display: flex;
  gap: 8px;
  z-index: 20;
}

.print-btn {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  font-size: 15px;
  color: #64748b;
  cursor: pointer;
  min-width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  transition: all 0.2s;
  gap: 7px;
  padding: 0 14px;
}

.print-btn:hover {
  background: #f1f5f9;
  color: #475569;
  border-color: #cbd5e1;
}

.print-label {
  font-size: 15px;
  font-weight: 600;
}

@media print {
  .print-btn, .modal-actions {
    display: none !important;
  }
}

.close-btn {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  font-size: 20px;
  color: #64748b;
  cursor: pointer;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  transition: all 0.2s;
}

.close-btn:hover {
  background: #f1f5f9;
  color: #475569;
  border-color: #cbd5e1;
}

/* ... le reste de ton CSS reste inchangé ... */
.medical-header {
  padding: 24px 32px 18px;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  border-bottom: 1px solid #e2e8f0;
}

.chart-title {
  font-size: 22px;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 6px 0;
  font-family: 'Inter', system-ui, sans-serif;
}

.session-duration {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  color: #64748b;
  font-weight: 500;
}

.chart-section {
  padding: 20px 32px 16px;
  background: #ffffff;
}

.chart-legend {
  display: flex;
  gap: 32px;
  margin-bottom: 16px;
  justify-content: center;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 8px;
}

.legend-indicator {
  width: 20px;
  height: 3px;
  border-radius: 2px;
}

.legend-indicator.hrv {
  background: #0d6efd;
}

.legend-indicator.coherence {
  background: #dc3545;
}

.legend-label {
  font-size: 13px;
  color: #475569;
  font-weight: 600;
}

.chart-wrapper {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 16px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.medical-chart {
  width: 100%;
  height: 240px;
  font-family: 'Inter', system-ui, sans-serif;
}

.axis-label {
  font-size: 10px;
  fill: #94a3b8;
  text-anchor: middle;
  font-weight: 500;
}

.y-axis .axis-label {
  text-anchor: end;
}

/* Dashboard horizontal - Amélioration pour répartition équitable */
.dashboard-inline {
  display: flex;
  align-items: center;
  padding: 20px 32px;
  background: #f8fafc;
  border-top: 1px solid #e2e8f0;
  gap: 0;
  min-height: 100px;
  overflow-x: auto;
}

/* Cartes de stats - Prennent maintenant 1/3 de l'espace chacune */
.stat-card {
  display: flex;
  align-items: center;
  gap: 12px;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 20px;
  flex: 1;
  margin: 0 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  justify-content: center;
}

.stat-card:first-child {
  margin-left: 0;
}

.stat-card:last-child {
  margin-right: 0;
}

.stat-visual {
  width: 50px;
  height: 50px;
  flex-shrink: 0;
}

.mini-gauge {
  width: 100%;
  height: 100%;
  transform: rotate(-90deg);
}

.gauge-bg {
  fill: none;
  stroke: #f1f5f9;
  stroke-width: 4;
}

.gauge-fill {
  fill: none;
  stroke-width: 4;
  stroke-linecap: round;
  transition: stroke-dashoffset 0.8s ease;
}

.gauge-fill.blue {
  stroke: #0d6efd;
}

.gauge-fill.red {
  stroke: #dc3545;
}

.stat-content {
  flex: 1;
}

.stat-value {
  font-size: 24px;
  font-weight: 700;
  margin-bottom: 2px;
  font-family: 'Inter', system-ui, sans-serif;
}

.stat-card.bpm .stat-value {
  color: #0d6efd;
}

.stat-card.coherence .stat-value {
  color: #dc3545;
}

.stat-label {
  font-size: 12px;
  color: #64748b;
  font-weight: 500;
  line-height: 1.2;
}

/* Zones en ligne - Maintenant flexible et équilibrée */
.zones-inline {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 20px;
  flex: 1;
  margin: 0 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  justify-content: center;
}

.zones-label {
  font-size: 12px;
  color: #64748b;
  font-weight: 600;
  text-align: center;
  margin-bottom: 4px;
}

.zones-bars {
  display: flex;
  align-items: end;
  gap: 16px;
  height: 50px;
  justify-content: center;
}

.zone-mini {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  width: 36px;
}

.zone-bar {
  width: 14px;
  border-radius: 7px 7px 2px 2px;
  transition: height 0.8s ease;
  min-height: 4px;
  max-height: 40px;
}

.zone-mini.optimal .zone-bar {
  background: linear-gradient(to top, #059669, #10b981);
}

.zone-mini.moderate .zone-bar {
  background: linear-gradient(to top, #d97706, #f59e0b);
}

.zone-mini.low .zone-bar {
  background: linear-gradient(to top, #dc2626, #ef4444);
}

.zone-value {
  font-size: 11px;
  font-weight: 700;
  color: #374151;
}

.zone-name {
  font-size: 10px;
  color: #64748b;
  font-weight: 500;
  text-align: center;
}

/* Pied de page */
.modal-footer {
  padding: 16px 32px;
  background: #f8fafc;
  border-top: 1px solid #e2e8f0;
  text-align: center;
}

/* Transitions */
.fade-zoom-enter-active, .fade-zoom-leave-active {
  transition: all 0.3s ease;
}

.fade-zoom-enter-from, .fade-zoom-leave-to {
  opacity: 0;
  transform: scale(0.95);
}

/* Responsive - Améliorations */
@media (max-width: 1200px) {
  .dashboard-inline {
    gap: 8px;
  }
  
  .stat-card, .zones-inline {
    margin: 0 6px;
    padding: 16px;
  }
  
  .stat-value {
    font-size: 20px;
  }
}

@media (max-width: 1024px) {
  .dashboard-inline {
    flex-direction: column;
    gap: 16px;
    align-items: stretch;
  }
  
  .stat-card, .zones-inline {
    margin: 0;
    flex: none;
  }
}

@media (max-width: 768px) {
  .replay-modal-card.medical-modern {
    width: 98vw;
    max-height: 98vh;
  }
  
  .medical-header, .chart-section, .dashboard-inline, .modal-footer {
    padding-left: 20px;
    padding-right: 20px;
  }
  
  .dashboard-inline {
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
  }
  
  .stat-card, .zones-inline {
    width: 100%;
  }
}

/* Pour éviter le chevauchement sur mobile */
@media (max-width: 600px) {
  .medical-header {
    padding-top: 60px;
  }
}

.btn-save {
  background-color: #28a745;
  color: white;
  border: none;
  border-radius: 8px;
  padding: 12px 24px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  margin-right: 10px;
}

.btn-save:hover {
  background-color: #218838;
}

.btn-close-analyse {
  background-color: #64748b;
  color: white;
  border: none;
  border-radius: 8px;
  padding: 12px 24px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-close-analyse:hover {
  background-color: #475569;
}

.toggle-comment-btn {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  color: #64748b;
  font-size: 14px;
  font-weight: 600;
  border-radius: 7px;
  padding: 7px 14px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 7px;
  transition: background 0.2s, border-color 0.2s;
  margin-bottom: 0;
  margin-top: 0;
}
.toggle-comment-btn:hover {
  background: #f1f5f9;
  border-color: #cbd5e1;
  color: #475569;
}
.comment-collapse {
  margin-top: 10px;
}
.fade-enter-active, .fade-leave-active {
  transition: opacity 0.2s;
}
.fade-enter-from, .fade-leave-to {
  opacity: 0;
}
</style>